import {Suspense, useEffect, useState} from "react";
import { motion } from "framer-motion";
import Loading from "../Loading";
import {
    Header,
    Sidebar,
    ChartIcon,
    MarketingIcon,
    RecruitIcon,
    UserIcon,
    Settings,
    Dropdown,
    Alert,
    Button, TextInput
} from "omoplay";
import {useLocation, useNavigate} from "react-router-dom";
import {useRecoilValue} from "recoil";
import {accountSelector, channelSelector, sidebarState} from "../../store/selector/selector";
import {accept, logout, reject} from "../../api/account/account.api";
import AssetIcon from "../../assets/images/icon.asset.svg"
import Browserpopup from "../../assets/images/icon.browser.popup.svg";
import MessageIcon from "../../assets/images/message_icon.svg";
import {getProjectList} from "../../api/project/project.api";
import {SERVICE, hasAdminGrant, hasService} from "../../libs/permissions";

const Layout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const account = useRecoilValue(accountSelector)
    const channel = useRecoilValue(channelSelector)
    const openSidebar = useRecoilValue(sidebarState)

    const [showBrowserPopup, setShowBrowserPopup] = useState(false)
    const [collapsedPadding, setCollapsedPadding] = useState("250px")

    /*
      🔴 **`account` 는 비어 있을 수 있다.** (2026-09-14)

      `GET /account` 가 실패하면 `getAccount` 가 `{}` 를, 이어서 `accountSelector` 가 atom 기본값 `{}` 를
      돌려준다. 그 값으로 이 컴포넌트가 렌더되는데 **이 앱에는 ErrorBoundary 가 한 곳도 없다** —
      렌더 중 예외 한 번이면 React 가 트리 전체를 언마운트해 **문구 없는 완전한 흰 화면**이 된다.
      실제로 그렇게 됐다: 종전 `account.channels.length` 가 `undefined.length` 로 터졌다.

      🔑 `fetch.js` 가 502/504 를 배너로 잡아 주게 됐지만, **열거하지 않은 상태코드는 언제든 새로 생긴다.**
      여기가 마지막 그물이다 — 아래 코드는 `account` 가 `{}` 여도 절대 던지지 않아야 한다.
    */
    const channels = account?.channels ?? []

    const currentChannel = channels.find(ch => ch.id === channel)
    const permissions = currentChannel?.permissions
    const showConnect = hasService(permissions, SERVICE.CONNECT)
    const showScreen = hasService(permissions, SERVICE.SCREEN)
    /*
      판정은 전부 `libs/permissions.js` 에 있다(2026-09-22 정리).

      ⚠️ 종전 `permissions?.[0]?.grant === "ADMIN" || ... === "OWNER"` 였다. 문제가 둘이었다 —
        · `permissions[0]` 은 **순서가 보장되지 않는다.** 한 채널은 권한 행을 여러 개
          (`Owner`·`Connect`·`Screen`) 가지는데 서버 조회에 `ORDER BY` 가 없었다.
          이제 `hasAdminGrant` 가 **행 하나라도** 관리 등급이면 참으로 본다.
        · 옵셔널 체이닝이 방어가 되지 않았다 — `permissions` 가 **빈 배열**이면
          체이닝이 멈추지 않아 `permissions[0]` 이 `undefined` 이고 `.grant` 에서 터졌다.
          권한이 비어 있는 채널로 세션을 바꾸면 그것만으로 흰 화면이었다.
    */
    const isAdmin = hasAdminGrant(permissions)

    /*
      내부 관리자(system) 계정 여부.

      서버가 `GET /account` 응답에 담아 내려주는 값이고, 판정 기준은
      `account_permission.service_type = 'System'` **한 곳**이다
      (redirect-service `AccountPermissionService.isSystemAdmin`).

      🔴 계정 id 를 코드에 박지 말 것 — 운영자를 넣고 빼려면 배포가 필요하고
      판정 근거가 DB 와 코드 두 군데로 갈린다.
      ✅ 2026-09-22 에 남아 있던 `account.id === "4217583917"` 두 곳을 이 값으로 옮겨
         없앤다 — 이 파일 아래쪽 PENDING 승인/반려 버튼과 `AIAssistant.js`.
         이제 이 앱에 계정 id 하드코딩은 없다. 새 가림도 전부 이 값을 쓴다.

      ⚠️ 화면 가림은 편의이고 강제가 아니다. 실제 차단은 서버 `SecurityConfig` 의
      경로 규칙이 한다 — `/sysadmin/**`·`/system/**`·`/wizard/**`,
      AI 생성 3종(`/ai/generate/project`·`/ai/generate/event`·`/automate-message/ai/generate`),
      그리고 2026-09-22 에 추가한 승인/반려(`/account/accept`·`/account/reject`).
    */
    const isSystemAdmin = account?.isSystemAdmin === true

    const [rejectValue, setRejectValue] = useState("")

    if (channels.length > 0 && !channels.map(channel => channel.id).includes(channel)) {
        localStorage.setItem("channelState", channels[0].id)
        window.location.href = "/"
    }

    useEffect(() => {
        setShowBrowserPopup(openSidebar)
        setCollapsedPadding(openSidebar ? "90px" : "250px")
    }, [openSidebar]);

    /*
      팝업은 nav 를 뺀 **콘텐츠 영역 안에서** 가운데에 둔다(2026-10-02 사용자 지시).

      nav(`omoplay` Sidebar, `.hd-sidebar`)는 `position:fixed; z-index:5000` 이고 모든 팝업은 그보다 낮다
      (`z-[50]`~`z-[2000]`). 팝업이 **브라우저 전체 폭**의 가운데에 놓이면, 창이 좁을 때 왼쪽이 nav 밑으로 들어갔다.
      ⇒ 본문 왼쪽 여백(= nav 폭, 펼침 250px / 접힘 90px)을 CSS 변수 `--nav-offset` 로 알리고,
         `common.css` 의 전역 규칙이 전체 화면 오버레이(`.fixed.inset-0`)·라이브러리 모달의 시작점을 그만큼 옮긴다.
      🔑 팝업마다 고치지 않는다 — 23곳 이상이고, 새 팝업에서 또 빠진다.
      Layout 이 없는 화면(고지서·카드 등록 링크 등)은 변수가 없어 0 이다(종전과 같다).
    */
    useEffect(() => {
        const root = document.documentElement
        root.style.setProperty("--nav-offset", collapsedPadding)
        return () => root.style.removeProperty("--nav-offset")
    }, [collapsedPadding]);

    /*
      프로젝트가 0건인 Connect 계정을 초기 세팅 화면으로 보낸다.

      ## 2026-09-09 — 관리자 계정으로 한정

      `/init/setup` 은 'AI 빠른 자동화 일괄 세팅' + 'AI 이벤트 생성' + 스티커 생성이
      한 화면에 붙은 구성이다(`InitializeSetup.js`). 고객이 이 화면에서 AI 생성을 자유롭게
      누르면서 **불필요한 AI 비용과 CS** 가 발생했다 ⇒ AI 생성은 '관리자 초기 세팅용' 으로
      용도를 좁혔고, 이 자동 이동도 함께 관리자로 한정한다.

      🔴 **부수 효과를 알고 넣는다** — 신규 고객이 초기 세팅 화면으로 자동 유입되지 않는다.
      프로젝트가 없는 고객은 기본 화면에 머문다. 초기 세팅은 설치 진행자/관리자의 작업이 된다
      (`/sysadmin/wizard/init-setting` 트랙과 방향이 같다).

      ⚠️ 라우트 자체도 관리자에게만 등록된다(`App.js`). 여기서만 막으면 주소를 직접 쳤을 때
      화면은 열리고 API 만 403 이 되어 '빈 화면 + 콘솔 에러' 가 된다.
    */
    useEffect(() => {
        if (isSystemAdmin && showConnect && window.location.pathname !== '/init/setup' && window.location.pathname !== '/payments') {
            getProjectList().then(({data}) => {
                if (data.length === 0) {
                    window.location.href = '/init/setup'
                }
            })
        }
    }, [])

    const playChildren = []

    /*
      'AI 이벤트 생성' — 2026-09-09 부터 내부 관리자 전용.
      고객이 임의로 실행해 AI 비용·CS 가 발생했고, 기능의 용도를 '초기 세팅' 으로 좁혔다.
      서버도 `/ai/generate/project`·`/ai/generate/event` 를 sysadmin 전용으로 막았으므로
      여기서 메뉴를 남겨두면 눌러도 403 이 난다.
    */
    if (showConnect && isSystemAdmin) {
        playChildren.push(
            {
                label: 'AI 이벤트 생성',
                active: location.pathname.startsWith("/ai/setup"),
                onClick: () => navigate("/ai/setup")
            }
        )
    }

    playChildren.push(
        {
            label: '프로젝트',
            active: location.pathname.startsWith("/project"),
            onClick: () => navigate("/project")
        },
        {
            label: '이벤트',
            active: location.pathname.startsWith("/event"),
            onClick: () => navigate("/event")
        },
        {
            label: '혜택',
            active: location.pathname.startsWith("/benefit"),
            onClick: () => navigate("/benefit")
        }
    )

    if (showConnect) {
        playChildren.push(
            {
                label: '고객 진입점 (QR)',
                active: location.pathname.startsWith("/spots"),
                onClick: () => navigate("/spots")
            }
        )
    }

    const marketingChildren = [
        {
            label: '1회성 메시지',
            active: location.pathname.startsWith("/volatility-message"),
            onClick: () => navigate("/volatility-message")
        },
        {
            label: '자동화 메시지',
            active: location.pathname.startsWith("/automate-message"),
            onClick: () => navigate("/automate-message")
        },
    ]

    if (isAdmin) {
        marketingChildren.push(
            {
                label: '메시지 캘린더',
                active: location.pathname.startsWith("/message/calendar"),
                onClick: () => navigate("/message/calendar")
            }
        )
    }

    const performedChildren = []

    /*
      '성과 보드' 하위 메뉴 (2026-09-09 명칭·순서 정리)

      🔴 여기의 `label` 이 곧 화면 상단 헤더 제목이다 — 아래 `const title` 이
         활성 메뉴의 label 을 그대로 쓴다. label 을 고치면 헤더 제목도 같이 바뀐다.

      명칭을 바꾼 이유:
       · '메시지 통계' → '자동화 메시지 성과'
         이 화면은 `automate_message` → `automate_message_statistics` 사슬만 집계한다.
         1회성 메시지는 어느 지표에도 들어가지 않는데 이름이 '메시지 통계' 라
         "1회성으로 쿠폰을 보냈는데 왜 0 이냐" 는 오해를 만들었다(2026-09-08 신고).
         근거 = `_ai-runs/2026-09-08_001_message_statistics_empty_and_kakao_user_db_load/002_*.md §1`
       · '메시지 리포트' → '메시지 발송 로그'
         발송 1건마다의 원장(성공/실패·수신자·발송시각)이라 '리포트'(=집계)로 읽히면
         위 '성과' 화면들과 성격이 구분되지 않는다.
       · '성과 대시보드' → '통합 성과 대시보드'
         자동화·1회성·온보딩을 모두 합산하는 유일한 화면이라 '자동화 메시지 성과' 와 대비시킨다.

      순서는 넓은 집계 → 좁은 집계 → 원장 순이다.
    */
    if (isAdmin) {
        performedChildren.push(
            {
                label: '통합 성과 대시보드',
                active: location.pathname.startsWith("/dashboard"),
                onClick: () => navigate("/dashboard")
            },
            {
                label: '자동화 메시지 성과',
                active: location.pathname.startsWith("/statistics/message"),
                onClick: () => navigate("/statistics/message")
            },
            {
                label: '온라인 매출',
                active: location.pathname.startsWith("/online/orders"),
                onClick: () => navigate("/online/orders")
            },
            {
                label: '메시지 발송 로그',
                active: location.pathname.startsWith("/messages"),
                onClick: () => navigate("/messages")
            }
            /*
            {
                label: 'AI 어시스턴트(Beta)',
                active: ["/agent/assistant"].includes(location.pathname),
                onClick: () => navigate("/agent/assistant")
            },
            */
        )
    }


    const productChildren = []

    if (showConnect && isAdmin) {
        productChildren.push(
            {
                label: '제품 목록',
                active: location.pathname === "/product",
                onClick: () => navigate("/product")
            }
        )
    }

    const customerChildren = [
        {
            label: '카카오 고객',
            active: ["/users"].includes(location.pathname),
            onClick: () => navigate("/users")
        },
        {
            label: '고객 전환 통계',
            active: location.pathname.startsWith("/users-statistics"),
            onClick: () => navigate("/users-statistics")
        }
    ]

    /*
    if (isAdmin) {
        customerChildren.unshift(
            {
                label: '통합 회원',
                active: ["/customers"].includes(location.pathname),
                onClick: () => navigate("/customers")
            }
        )
    }
    */

    const settingsChildren = [
        /*
        {
            label: '예치금 / AI 크레딧',
            active: ["/credit", "/credit/detail"].includes(location.pathname),
            onClick: () => navigate("/credit")
        },
        */
    ]

    if (showScreen) {
        settingsChildren.push(
            {
                label: '간편 로그인 서비스',
                active: location.pathname.startsWith("/auth/screen"),
                onClick: () => navigate("/auth/screen")
            }
        )
    }

    settingsChildren.push(
        {
            label: '결제 정보',
            active: location.pathname.startsWith("/payments"),
            onClick: () => navigate("/payments")
        }
    )

    const sideBarItems = [
        {
            title: '플레이',
            icon: <RecruitIcon />,
            show: showConnect || showScreen,
            open: ["/project", "/spots", "/event", "/benefit", "/ai/setup"].some(path => location.pathname.startsWith(path)),
            children: playChildren,
        },
        {
            title: '마케팅',
            icon: <MarketingIcon />,
            show: showConnect,
            open: ["/volatility-message", "/volatility-message/detail", "/ai/volatility-message", "/automate-message", "/message/calendar", "/onboarding-message", "/message/onetime/edit"].includes(location.pathname),
            children: marketingChildren,
        },
        {
            title: '성과 보드',
            icon: <ChartIcon />,
            show: showConnect,
            open: ["/messages", "/online/orders", "/agent/assistant", "/statistics/message", "/dashboard"].includes(location.pathname),
            children: performedChildren,
        },
        {
            title: '고객',
            icon: <UserIcon />,
            show: showConnect,
            open: ["/user/detail", "/users", "/users-statistics", "/customers"].includes(location.pathname),
            children: customerChildren
        },
        {
            title: '자산',
            icon: <img src={AssetIcon} alt="asset" />,
            show: showConnect,
            open: ["/brand", "/product", "/product/detail"].includes(location.pathname),
            children: productChildren,
        },
        /*
        {
            title: '에이전트',
            icon: <img src={AgentIcon} alt="agent" />,
            show: showAgent,
            open: ["/agent/message", "/agent/message/automation", "/agent/message/detail"].includes(location.pathname),
            children: agentChildren
        },
        */
    ]

    if (showConnect) {
        sideBarItems.push(
            {
                title: "적립",
                icon: <img src={MessageIcon} alt="메시지 아이콘" />,
                /*
                  🔑 **신청 여부와 무관하게 낸다**(2026-09-22 사용자 지시).

                  적립은 부가 판매 상품이라 **있다는 것을 고객이 알아야 팜린다.**
                  그래서 메뉴는 남기고, 들어갔을 때 "별도 신청이 필요합니다" 안내로 바뀜다
                  (`ServiceGuard` → `PointServiceGuard`).
                  ⇒ **메뉴 노출 여부와 기능 동작 여부는 서로 다른 판정이다.** 섮지 말 것.

                  ⚠️ 종전에는 `&& isAdmin` 이 붙어 있었다. 그건 두 번 틀렸다 —
                     고객사는 사실상 admin 계정 하나로 쓰므로 등급으로는 아무것도 갈리지 않았고,
                     정작 갈려야 할 '적립을 신청했는가' 는 아무 데서도 보지 않았다.
                */
                show: showConnect || showScreen,
                open: ["/point/users", "/point/config"].includes(location.pathname),
                children: [
                    {
                        label: '포인트 적립 고객 화면',
                        active: location.pathname.startsWith("/point/customer"),
                        onClick: () => navigate("/point/customer")
                    },
                    {
                        label: '포인트 적립 관리자 화면',
                        active: location.pathname.startsWith("/point/view"),
                        onClick: () => navigate("/point/view")
                    },
                    {
                        label: '포인트 고객 조회',
                        active: location.pathname.startsWith("/point/users"),
                        onClick: () => navigate("/point/users")
                    },
                    {
                        label: '포인트 설정',
                        active: location.pathname.startsWith("/point/config"),
                        onClick: () => navigate("/point/config")
                    }
                ]
            }
        )
    }

    sideBarItems.push(
        {
            title: "설정",
            icon: <Settings />,
            show: showConnect || showScreen,
            open: ["/settings", "/credit", "/credit/detail", "/payments", "/auth/screen"].includes(location.pathname),
            children: settingsChildren
        }
    )

    const pageVariants = {
        initial: { opacity: 0, y: 0 },
        animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
    };

    const excludeHeaders = [
        { label: "서비스 설정", location: "/services" }, { label: "고객 상세", location: "/user/detail" },
        { label: "제품 상세", location: "/product/detail" }, { label: "AI 메시지 상세", location: "/agent/message/detail" },
        { label: "시스템 관리자", location: "/sysadmin" }, { label: "AI 어시스턴트", location: "/agent/assistant" },
        { label: "메시지 수정", location: "/message/onetime/edit" }, { label: "대시보드", location: "/dashboard" },
        { label: "스티커", location: "/sticker" }, { label: "초기 세팅", location: "/init/setup" }
    ]

    // ⚠️ 종전: `header.location.includes(location.pathname)` + `[0].label`
    //    하위 경로(예: /sysadmin/dashboard)에서는 "/sysadmin".includes("/sysadmin/dashboard") 가 false 라
    //    매칭이 0건이 되고, `[0].label` 접근에서 **TypeError 로 화면 전체가 죽었다**(?? 는 그 앞에서 못 막는다).
    //    → 방향을 뒤집어 startsWith 로 판정하고, 가장 긴(구체적인) 경로를 우선한다. 접근도 옵셔널 체이닝.
    const matchedHeader = excludeHeaders
        .filter(header => location.pathname === header.location || location.pathname.startsWith(`${header.location}/`))
        .sort((a, b) => b.location.length - a.location.length)[0]

    const title = sideBarItems.flatMap(item => item.children).filter(child => child.active).map(child => child.label)[0]
        ?? matchedHeader?.label ?? ""

    return (
        <>
            {
                showBrowserPopup && (
                    /*
                      좁은 화면 안내 배너 — `z-[6000]`(2026-08-21)

                      종전에는 **z-index 가 아예 없어** DOM 순서로만 쌓였다. 이 배너는 Layout 최상단에
                      렌더되므로 뒤에 오는 `position` 요소들에 **가려지거나 반쯤 겹쳐 보였다**(사용자 보고).

                      6000 은 앱에서 쓰는 다른 값들보다 확실히 위다 —
                      모달 `z-[100]`·`z-[2000]`, 툴팁 `z-[60]`, `common.css` 최대 `9999`.
                      (채널 전환 안내 바만 `2147483647` 로 더 위인데, 그건 "모든 요청이 차단됐다" 는
                       더 급한 경고라 그대로 두는 것이 맞다.)
                    */
                    <div className="fixed bottom-0 w-full z-[6000] flex-center-all text-center flex-col gap-2 bg-white p-[32px]">
                        <div>
                            <img src={Browserpopup} alt="browser-popup" />
                        </div>
                        <div className="text-primary text-[20px] font-bold">PC 브라우저로 이용 권장 드립니다.</div>
                        <div>
                            오모플레이는 PC 크롬 브라우저에<br/>
                            최적화 되어 있습니다.
                        </div>
                        <div>
                            <Button variants="primary" value="확인" onClick={() => setShowBrowserPopup(false)} />
                        </div>
                    </div>
                )
            }
            <div className="layout bg-slate-200">
                <Sidebar collapsed={openSidebar} items={sideBarItems} topElement={(
                    <div className="flex-col gap-[12px] p-[20px] pt-0">
                        {
                            collapsedPadding === "250px" ? (
                                <>
                                    <Dropdown variants="default"
                                              showSearch={true}
                                              titleStyle={{backgroundColor: "#FFF", color: "black", borderRadius: "8px"}}
                                              boxStyle={{overflow: "scroll", color: "black"}}
                                              initialValue={channel}
                                              items={channels.length === 0 ? [{}] : channels.map(channel => {
                                                  return ({
                                                      label: channel.name,
                                                      value: channel.id
                                                  })
                                              })}
                                              onChanged={(items) => {
                                                  if (channel !== Number(items[0].value)) {
                                                      localStorage.setItem("channelState", items[0].value)
                                                      window.location.reload()
                                                  }
                                              }}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="text-white overflow-hidden text-ellipsis whitespace-nowrap text-center">
                                        {currentChannel?.name}
                                    </div>
                                </>
                            )
                        }
                        <div className="border-b" style={{borderColor: "rgba(255, 255, 255, 0.16)"}}></div>
                    </div>
                )}
                         onCollapsed={(collapsed) => {
                             setCollapsedPadding(collapsed ? "90px" : "250px")
                         }}
                />
                <div className="main-layout" style={{paddingLeft: collapsedPadding}}>
                    <div className="fixed w-full" style={{paddingRight: collapsedPadding, zIndex: "10"}}>
                        <Header
                            title={title}
                            nickName={account.name}
                            items={[
                                {label: '서비스 설정', onclick: () => navigate("/services")},
                                {label: "로그아웃", onclick: () => logout()},
                            ]}
                        />
                    </div>
                    <motion.div
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={pageVariants}
                    >
                        <div className="layout-container" style={{paddingTop: "56px"}}>
                            <div className="inner-layout">
                                <Suspense fallback={
                                    <div className="w-full text-center py-8 justify-center">
                                        <Loading className={"w-20 h-20 mx-auto"}/>
                                    </div>
                                }>
                                    {
                                        permissions?.find(permission => permission.grant === "PENDING") && (
                                            <div className="pb-[16px]">
                                                <Alert type="warning" withIcon={true}
                                                       title="서비스가 신청 단계이므로 일부 서비스가 동작하지 않을 수 있습니다.">
                                                </Alert>
                                                {
                                                    /*
                                                      승인 / 반려 는 **내부 관리자 전용**이다(2026-09-22 사용자 지시).

                                                      🔴 종전에는 `account.id === "4217583917"` 로 가렸다. 두 가지가 틀렸다 —
                                                        · 계정 id 를 코드에 박으면 운영자를 넣고 빼려면 배포가 필요하고
                                                          판정 근거가 DB 와 코드 두 군데로 갈린다.
                                                        · 그 계정 하나만 아니라 **내부 관리자 전체**가 할 수 있어야 한다.
                                                      ⚠️ 반대로 고객 계정은 **자기 신청을 스스로 승인할 수 없어야** 한다.
                                                         `isSystemAdmin` 은 계정 단위 판정(`account_permission.service_type='System'`)라
                                                         채널을 전환해도 변하지 않는다.
                                                      ⚠️ 화면 가림은 편의일 뿐이다 — 실제 차단은 서버가 한다
                                                         (`accept`·`reject` 는 `/account/**` 가 아니라 관리자 경로여야 한다 — 아래 TODO).
                                                    */
                                                    isSystemAdmin && (
                                                        <div className="flex-align-center">
                                                            <Button variants="primary" value="승인" onClick={() => {
                                                                if (window.confirm("승인 알림톡이 전송됩니다.")) {
                                                                    accept(channel, rejectValue).then(() => window.location.reload())
                                                                }
                                                            }} />
                                                            <Button variants="danger" value="반려" onClick={() => {
                                                                if (window.confirm("사유와 함께 알림톡이 전송됩니다.")) {
                                                                    reject(channel, rejectValue).then(() => window.location.reload())
                                                                }
                                                            }} />
                                                            <div className="w-[500px]">
                                                                <TextInput value={rejectValue} onChange={(e) => setRejectValue(e.target.value)} />
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )
                                    }
                                    {children}
                                </Suspense>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </>
    );
};
export default Layout;
